import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  FileMinus2,
  FilePenLine,
  FilePlus2,
  List,
  Search
} from 'lucide-react'
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { diffLines, structuredPatch } from 'diff'
import {
  Decoration,
  Diff as ReactDiffView,
  Hunk,
  expandFromRawCode,
  getChangeKey,
  getCollapsedLinesCountBetween,
  isDelete,
  isInsert,
  markEdits,
  parseDiff,
  tokenize,
  type ChangeData,
  type DiffType as ReactDiffType,
  type FileData,
  type HunkData,
  type HunkTokens
} from 'react-diff-view'
import refractor from 'refractor/core'
import bash from 'refractor/lang/bash'
import csv from 'refractor/lang/csv'
import dataweave from 'refractor/lang/dataweave'
import java from 'refractor/lang/java'
import json from 'refractor/lang/json'
import jsx from 'refractor/lang/jsx'
import markdown from 'refractor/lang/markdown'
import python from 'refractor/lang/python'
import sql from 'refractor/lang/sql'
import toml from 'refractor/lang/toml'
import tsx from 'refractor/lang/tsx'
import typescript from 'refractor/lang/typescript'
import yaml from 'refractor/lang/yaml'
import 'react-diff-view/style/index.css'
import 'prism-color-variables/variables.css'
import { Typography } from '~/components/common/Typography'
import { type Diff, DiffType } from '~/protos/agents'
import { cm } from '~/utils'
import { getDiffPath } from '~/utils/diffUtils'

export interface DiffReviewPanelProps {
  activePath?: string
  /**
   * Default file path to select. Used when ``activePath`` is uncontrolled.
   *
   * Contract: a non-empty value overrides the panel's internal selection
   * whenever the prop changes (the panel watches ``initialActivePath``
   * via a ``useEffect`` and re-syncs ``internalActivePath``). This
   * matches the modal-open-with-new-file pattern used by
   * ``RefactorFileViewerSlide`` callers (``Artifact2CodeSlide`` /
   * ``SourceFilesLoadingSlide`` re-open the modal with a new path on
   * every click) and intentionally trades "stable user selection
   * across parent re-renders" for "panel follows the latest opened
   * file". If a future caller wires this prop to a value that changes
   * on every render (e.g. URL params), the user's mid-modal navigation
   * would be overridden — pass a stable ``key`` to re-mount the panel
   * instead.
   */
  initialActivePath?: string
  diffs: Diff[]
  className?: string
  fillHeight?: boolean
  headerActions?: ReactNode
  hideFileSidebar?: boolean
  onActivePathChange?: (path: string) => void
}

interface DiffFileMeta {
  diff: Diff
  path: string
  fileName: string
  directory: string
  extension: string
  addedLines: number
  removedLines: number
  contentChars: number
  totalLines: number
}

const HUGE_DIFF_LINE_LIMIT = 50000
const HUGE_DIFF_CHAR_LIMIT = 2_000_000
const DIFF_REVIEW_CODE_FONT = 'Geist Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, monospace'
const DIFF_CONTEXT_LINES = 5
const DIFF_HIGHLIGHT_LINE_LIMIT = 5000
const DIFF_EXPAND_FULL_LINE_LIMIT = 80
const DIFF_EXPAND_EDGE_LINE_COUNT = 24

interface ChangeGroup {
  anchorId: string
  modifiedLineNumber: number
  selectedChangeKeys: string[]
}

interface DiffCodeViewerProps {
  activeChangeGroup?: ChangeGroup
  file: DiffFileMeta
  parsedFile: FileData
}

type DiffFileFilter = 'all' | 'added' | 'modified' | 'deleted'

const diffReviewGrammars = [
  bash,
  csv,
  dataweave,
  java,
  json,
  jsx,
  markdown,
  python,
  sql,
  toml,
  tsx,
  typescript,
  yaml
]

for (const grammar of diffReviewGrammars) {
  refractor.register(grammar)
}

const diffReviewPanelCss = `
.landing-diff-scroll-container::-webkit-scrollbar:horizontal {
  display: block;
  height: 0.5rem;
}

.landing-diff-code-view {
  --diff-background-color: #ffffff;
  --diff-text-color: #24292f;
  --diff-font-family: ${DIFF_REVIEW_CODE_FONT};
  --diff-selection-background-color: #bfd7ff;
  --diff-selection-text-color: #0f172a;
  --diff-gutter-insert-background-color: #eaf5ec;
  --diff-gutter-insert-text-color: #1a7f37;
  --diff-gutter-delete-background-color: #fdebec;
  --diff-gutter-delete-text-color: #cf222e;
  --diff-gutter-selected-background-color: #fff8c5;
  --diff-gutter-selected-text-color: #24292f;
  --diff-code-insert-background-color: #eaf5ec;
  --diff-code-insert-text-color: #24292f;
  --diff-code-delete-background-color: #fdebec;
  --diff-code-delete-text-color: #24292f;
  --diff-code-insert-edit-background-color: rgba(31, 136, 61, 0.18);
  --diff-code-insert-edit-text-color: #116329;
  --diff-code-delete-edit-background-color: rgba(207, 34, 46, 0.16);
  --diff-code-delete-edit-text-color: #a40e26;
  --diff-code-selected-background-color: #fff8c5;
  --diff-code-selected-text-color: #24292f;
  --code-highlight-comment-color: #34823f;
  --code-highlight-prolog-color: #34823f;
  --code-highlight-doctype-color: #34823f;
  --code-highlight-cdata-color: #34823f;
  --code-highlight-tag-color: #8250df;
  --code-highlight-entity-color: #953800;
  --code-highlight-atrule-color: #0550ae;
  --code-highlight-url-color: #0969da;
  --code-highlight-selector-color: #116329;
  --code-highlight-string-color: #0a7f36;
  --code-highlight-property-color: #953800;
  --code-highlight-important-color: #cf222e;
  --code-highlight-punctuation-color: #57606a;
  --code-highlight-number-color: #0550ae;
  --code-highlight-function-color: #8250df;
  --code-highlight-class-name-color: #953800;
  --code-highlight-keyword-color: #cf222e;
  --code-highlight-boolean-color: #0550ae;
  --code-highlight-operator-color: #57606a;
  --code-highlight-char-color: #0a7f36;
  --code-highlight-regex-color: #953800;
  --code-highlight-variable-color: #953800;
  --code-highlight-constant-color: #0550ae;
  --code-highlight-symbol-color: #953800;
  --code-highlight-function-name-color: #8250df;
  --code-highlight-builtin-color: #8250df;
  --code-highlight-attr-name-color: #953800;
  --code-highlight-attr-value-color: #0550ae;
  --code-highlight-inserted-color: #116329;
  --code-highlight-deleted-color: #a40e26;
  --code-highlight-namespace-color: #57606a;
  background: #ffffff;
  color: #24292f;
  font-family: ${DIFF_REVIEW_CODE_FONT};
  font-size: 12.5px;
  font-feature-settings: "liga" 0, "calt" 0;
  line-height: 22px;
  min-height: 360px;
  tab-size: 2;
}

.landing-react-diff.diff {
  --diff-background-color: #ffffff;
  --diff-text-color: #24292f;
  --diff-font-family: ${DIFF_REVIEW_CODE_FONT};
  --diff-selection-background-color: #bfd7ff;
  --diff-selection-text-color: #0f172a;
  --diff-gutter-insert-background-color: #eaf5ec;
  --diff-gutter-insert-text-color: #1a7f37;
  --diff-gutter-delete-background-color: #fdebec;
  --diff-gutter-delete-text-color: #cf222e;
  --diff-gutter-selected-background-color: #fff8c5;
  --diff-gutter-selected-text-color: #24292f;
  --diff-code-insert-background-color: #eaf5ec;
  --diff-code-insert-text-color: #24292f;
  --diff-code-delete-background-color: #fdebec;
  --diff-code-delete-text-color: #24292f;
  --diff-code-insert-edit-background-color: rgba(31, 136, 61, 0.18);
  --diff-code-insert-edit-text-color: #116329;
  --diff-code-delete-edit-background-color: rgba(207, 34, 46, 0.16);
  --diff-code-delete-edit-text-color: #a40e26;
  --diff-code-selected-background-color: #fff8c5;
  --diff-code-selected-text-color: #24292f;
  --code-highlight-comment-color: #34823f;
  --code-highlight-prolog-color: #34823f;
  --code-highlight-doctype-color: #34823f;
  --code-highlight-cdata-color: #34823f;
  --code-highlight-tag-color: #8250df;
  --code-highlight-entity-color: #953800;
  --code-highlight-atrule-color: #0550ae;
  --code-highlight-url-color: #0969da;
  --code-highlight-selector-color: #116329;
  --code-highlight-string-color: #0a7f36;
  --code-highlight-property-color: #953800;
  --code-highlight-important-color: #cf222e;
  --code-highlight-punctuation-color: #57606a;
  --code-highlight-number-color: #0550ae;
  --code-highlight-function-color: #8250df;
  --code-highlight-class-name-color: #953800;
  --code-highlight-keyword-color: #cf222e;
  --code-highlight-boolean-color: #0550ae;
  --code-highlight-operator-color: #57606a;
  --code-highlight-char-color: #0a7f36;
  --code-highlight-regex-color: #953800;
  --code-highlight-variable-color: #953800;
  --code-highlight-constant-color: #0550ae;
  --code-highlight-symbol-color: #953800;
  --code-highlight-function-name-color: #8250df;
  --code-highlight-builtin-color: #8250df;
  --code-highlight-attr-name-color: #953800;
  --code-highlight-attr-value-color: #0550ae;
  --code-highlight-inserted-color: #116329;
  --code-highlight-deleted-color: #a40e26;
  --code-highlight-namespace-color: #57606a;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: auto;
  width: max-content;
  min-width: 100%;
  min-height: 0;
}

.landing-react-diff .diff-line {
  font-family: ${DIFF_REVIEW_CODE_FONT};
  font-size: 12.5px;
  line-height: 22px;
}

.landing-react-diff .diff-line:hover .diff-gutter,
.landing-react-diff .diff-line:hover .diff-code {
  background: #f6f8fa;
}

.landing-react-diff .diff-gutter-col {
  width: 44px;
}

.landing-react-diff .diff-gutter {
  border-right: 1px solid #e6eaf0;
  cursor: default;
  font-variant-numeric: tabular-nums;
  min-width: 44px;
  padding: 0 8px;
  text-align: right;
  user-select: none;
  white-space: nowrap;
}

.landing-react-diff .diff-gutter + .diff-gutter {
  border-right: 1px solid #dbe2eb;
}

.landing-react-diff-added-file col.diff-gutter-col:first-child,
.landing-react-diff-added-file .diff-line > .diff-gutter:first-child {
  display: none;
}

.landing-react-diff-deleted-file col.diff-gutter-col:nth-child(2),
.landing-react-diff-deleted-file .diff-line > .diff-gutter:nth-child(2) {
  display: none;
}

.landing-react-diff-added-file .diff-line > .diff-gutter:nth-child(2),
.landing-react-diff-deleted-file .diff-line > .diff-gutter:first-child {
  min-width: 40px;
  padding: 0 7px;
}

.landing-react-diff .diff-gutter-normal {
  color: #7d8590;
}

.landing-react-diff .diff-gutter-insert:first-child,
.landing-react-diff .diff-gutter-delete:first-child {
  box-shadow: inset 3px 0 0 currentColor;
}

.landing-react-diff-added-file .diff-line > .diff-gutter-insert:nth-child(2) {
  box-shadow: inset 3px 0 0 currentColor;
}

.landing-react-diff .diff-code {
  min-width: max-content;
  overflow: visible;
  padding: 0 16px;
  white-space: pre;
  word-break: normal;
  word-wrap: normal;
}

.landing-react-diff .diff-code-normal {
  background: #ffffff;
}

.landing-react-diff .diff-code-insert,
.landing-react-diff .diff-gutter-insert {
  background: #eaf5ec;
}

.landing-react-diff .diff-code-delete,
.landing-react-diff .diff-gutter-delete {
  background: #fdebec;
}

.landing-react-diff .diff-code-edit {
  border-radius: 3px;
  box-decoration-break: clone;
  padding: 0 1px;
}

.landing-react-diff .diff-gutter-selected,
.landing-react-diff .diff-code-selected {
  box-shadow: inset 0 0 0 1px rgba(9, 105, 218, 0.22);
}

.landing-react-diff .diff-decoration {
  background: #f6f8fa;
  font-family: Geist, ui-sans-serif, system-ui, sans-serif;
  user-select: none;
}

.landing-react-diff .diff-decoration-gutter {
  border-right: 1px solid #e6eaf0;
  background: #f6f8fa;
}

.landing-react-diff .diff-decoration-content {
  background: #f6f8fa;
  padding: 0;
}

.landing-diff-gap-button {
  align-items: center;
  background: #f1f3f6;
  border: 1px solid #e6eaf0;
  border-radius: 6px;
  color: #57606a;
  display: inline-flex;
  font-family: Geist, ui-sans-serif, system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  gap: 6px;
  height: 30px;
  margin: 5px 8px;
  padding: 0 12px;
}

.landing-diff-gap-button:hover {
  background: #eaeef2;
  color: #24292f;
}

.landing-diff-code-loading {
  color: #7d8590;
}

.landing-diff-file-popover {
  max-height: min(520px, calc(100vh - 180px));
}

@media (max-width: 768px) {
  .landing-react-diff .diff-gutter {
    min-width: 36px;
    padding: 0 5px;
  }

  .landing-react-diff .diff-code {
    padding: 0 10px;
  }
}

/* ── Dark mode ────────────────────────────────────────────────────────────────
 * The diff renderer is driven entirely by CSS custom properties plus a few
 * hardcoded light backgrounds. In dark mode we re-point every variable to a
 * GitHub-dark-style palette and override the literal light backgrounds so the
 * code area, gutters, and syntax highlighting all read correctly on a dark
 * surface (instead of the default white-bg / black-text combo). */
.dark .landing-diff-code-view,
.dark .landing-react-diff.diff {
  --diff-background-color: #1f1f23;
  --diff-text-color: #c9d1d9;
  --diff-selection-background-color: #2d4f7c;
  --diff-selection-text-color: #e6edf3;
  --diff-gutter-insert-background-color: rgba(46, 160, 67, 0.16);
  --diff-gutter-insert-text-color: #3fb950;
  --diff-gutter-delete-background-color: rgba(248, 81, 73, 0.16);
  --diff-gutter-delete-text-color: #f85149;
  --diff-gutter-selected-background-color: rgba(187, 128, 9, 0.22);
  --diff-gutter-selected-text-color: #e6edf3;
  --diff-code-insert-background-color: rgba(46, 160, 67, 0.16);
  --diff-code-insert-text-color: #c9d1d9;
  --diff-code-delete-background-color: rgba(248, 81, 73, 0.16);
  --diff-code-delete-text-color: #c9d1d9;
  --diff-code-insert-edit-background-color: rgba(46, 160, 67, 0.32);
  --diff-code-insert-edit-text-color: #aff5b4;
  --diff-code-delete-edit-background-color: rgba(248, 81, 73, 0.32);
  --diff-code-delete-edit-text-color: #ffdcd7;
  --diff-code-selected-background-color: rgba(187, 128, 9, 0.18);
  --diff-code-selected-text-color: #e6edf3;
  --code-highlight-comment-color: #8b949e;
  --code-highlight-prolog-color: #8b949e;
  --code-highlight-doctype-color: #8b949e;
  --code-highlight-cdata-color: #8b949e;
  --code-highlight-tag-color: #7ee787;
  --code-highlight-entity-color: #ffa657;
  --code-highlight-atrule-color: #79c0ff;
  --code-highlight-url-color: #a5d6ff;
  --code-highlight-selector-color: #7ee787;
  --code-highlight-string-color: #a5d6ff;
  --code-highlight-property-color: #79c0ff;
  --code-highlight-important-color: #ff7b72;
  --code-highlight-punctuation-color: #c9d1d9;
  --code-highlight-number-color: #79c0ff;
  --code-highlight-function-color: #d2a8ff;
  --code-highlight-class-name-color: #ffa657;
  --code-highlight-keyword-color: #ff7b72;
  --code-highlight-boolean-color: #79c0ff;
  --code-highlight-operator-color: #c9d1d9;
  --code-highlight-char-color: #a5d6ff;
  --code-highlight-regex-color: #ffa657;
  --code-highlight-variable-color: #ffa657;
  --code-highlight-constant-color: #79c0ff;
  --code-highlight-symbol-color: #ffa657;
  --code-highlight-function-name-color: #d2a8ff;
  --code-highlight-builtin-color: #d2a8ff;
  --code-highlight-attr-name-color: #7ee787;
  --code-highlight-attr-value-color: #a5d6ff;
  --code-highlight-inserted-color: #3fb950;
  --code-highlight-deleted-color: #f85149;
  --code-highlight-namespace-color: #c9d1d9;
}

.dark .landing-diff-code-view {
  background: #1f1f23;
  color: #c9d1d9;
}

.dark .landing-react-diff .diff-code-normal {
  background: #1f1f23;
}

.dark .landing-react-diff .diff-code-insert,
.dark .landing-react-diff .diff-gutter-insert {
  background: rgba(46, 160, 67, 0.16);
}

.dark .landing-react-diff .diff-code-delete,
.dark .landing-react-diff .diff-gutter-delete {
  background: rgba(248, 81, 73, 0.16);
}

.dark .landing-react-diff .diff-line:hover .diff-gutter,
.dark .landing-react-diff .diff-line:hover .diff-code {
  background: #26262b;
}

.dark .landing-react-diff .diff-gutter {
  border-right: 1px solid #33333a;
}

.dark .landing-react-diff .diff-gutter + .diff-gutter {
  border-right: 1px solid #3a3a40;
}

.dark .landing-react-diff .diff-gutter-normal {
  color: #6e7681;
}

.dark .landing-react-diff .diff-decoration,
.dark .landing-react-diff .diff-decoration-gutter,
.dark .landing-react-diff .diff-decoration-content {
  background: #26262b;
}

.dark .landing-react-diff .diff-decoration-gutter {
  border-right: 1px solid #33333a;
}

.dark .landing-diff-gap-button {
  background: #26262b;
  border: 1px solid #3a3a40;
  color: #9aa4b0;
}

.dark .landing-diff-gap-button:hover {
  background: #30303a;
  color: #e6edf3;
}

.dark .landing-diff-code-loading {
  color: #6e7681;
}
`

function countLines(value: string): number {
  if (!value) return 0
  const lines = value.split(/\r\n|\r|\n/)
  return lines[lines.length - 1] === '' ? lines.length - 1 : lines.length
}

function getPatchLineStats(patch: string) {
  if (!patch) return null

  let addedLines = 0
  let removedLines = 0
  for (const line of patch.split(/\r\n|\r|\n/)) {
    if (line.startsWith('+++') || line.startsWith('---')) continue
    if (line.startsWith('+')) addedLines += 1
    if (line.startsWith('-')) removedLines += 1
  }

  if (addedLines === 0 && removedLines === 0) return null
  return { addedLines, removedLines }
}

// Re-export the imported binding so external callers (LandingCoderTaskPanel,
// any existing consumers) keep working unchanged. New call sites should
// import directly from '~/utils/diffUtils'.
export { getDiffPath }

export function getFileName(path: string): string {
  return path.split('/').pop() || path
}

export function getDirectory(path: string): string {
  const index = path.lastIndexOf('/')
  return index > 0 ? path.slice(0, index) : ''
}

function getExtension(path: string): string {
  const name = getFileName(path)
  const index = name.lastIndexOf('.')
  return index >= 0 ? name.slice(index + 1).toUpperCase() : 'FILE'
}

export function getChangedLineStats(diff: Diff) {
  if (diff.diffType === DiffType.ADD) {
    return { addedLines: countLines(diff.bFileContent), removedLines: 0 }
  }
  if (diff.diffType === DiffType.DELETE) {
    return { addedLines: 0, removedLines: countLines(diff.aFileContent) }
  }

  const fromPatch = getPatchLineStats(diff.patchBA || '')
  if (fromPatch) return fromPatch

  const oldLineCount = countLines(diff.aFileContent)
  const newLineCount = countLines(diff.bFileContent)
  const contentChars = (diff.aFileContent || '').length + (diff.bFileContent || '').length
  if (
    Math.max(oldLineCount, newLineCount) > HUGE_DIFF_LINE_LIMIT ||
    contentChars > HUGE_DIFF_CHAR_LIMIT
  ) {
    return {
      addedLines: Math.max(newLineCount - oldLineCount, 0),
      removedLines: Math.max(oldLineCount - newLineCount, 0)
    }
  }

  let addedLines = 0
  let removedLines = 0
  for (const part of diffLines(diff.aFileContent || '', diff.bFileContent || '')) {
    const count = countLines(part.value || '')
    if (part.added) addedLines += count
    if (part.removed) removedLines += count
  }
  return { addedLines, removedLines }
}

function getDiffTypeConfig(diffType: DiffType) {
  switch (diffType) {
    case DiffType.ADD:
      return {
        label: 'Added',
        icon: FilePlus2,
        pillClass: 'bg-[#EAF7EE] text-[#1F883D] border-[#B7E4C7]'
      }
    case DiffType.DELETE:
      return {
        label: 'Deleted',
        icon: FileMinus2,
        pillClass: 'bg-[#FDECEF] text-[#CF222E] border-[#F5C2C7]'
      }
    case DiffType.MODIFY:
    default:
      return {
        label: 'Modified',
        icon: FilePenLine,
        pillClass: 'bg-[#FFF8C5] text-[#7D4E00] border-[#F0D98C]'
      }
  }
}

function splitSourceLines(value: string): string[] {
  if (!value) return []
  const lines = value.split(/\r\n|\r|\n/)
  if (lines[lines.length - 1] === '') lines.pop()
  return lines
}

function getRefractorLanguage(path: string): string {
  const extension = getExtension(path).toLowerCase()
  switch (extension) {
    case 'xml':
    case 'html':
      return 'xml'
    case 'css':
      return 'css'
    case 'json':
    case 'java':
    case 'csv':
    case 'sql':
    case 'toml':
    case 'tsx':
    case 'jsx':
      return extension
    case 'py':
      return 'python'
    case 'ts':
      return 'typescript'
    case 'js':
      return 'javascript'
    case 'yaml':
    case 'yml':
    case 'properties':
      return 'yaml'
    case 'md':
    case 'markdown':
      return 'markdown'
    case 'dwl':
    case 'dataweave':
      return 'dataweave'
    case 'sh':
    case 'shell':
      return 'bash'
    default:
      return 'plaintext'
  }
}

function getReactDiffType(diffType: DiffType): ReactDiffType {
  switch (diffType) {
    case DiffType.ADD:
      return 'add'
    case DiffType.DELETE:
      return 'delete'
    case DiffType.MODIFY:
    default:
      return 'modify'
  }
}

function formatHunkRange(start: number, lines: number): string {
  return `${start},${lines}`
}

function buildGitPatch(file: DiffFileMeta): string {
  const oldDisplayPath = file.diff.aFilePath || file.path
  const newDisplayPath = file.diff.bFilePath || file.path
  const oldPatchPath = file.diff.diffType === DiffType.ADD ? '/dev/null' : `a/${oldDisplayPath}`
  const newPatchPath = file.diff.diffType === DiffType.DELETE ? '/dev/null' : `b/${newDisplayPath}`
  const patch = structuredPatch(
    oldPatchPath,
    newPatchPath,
    file.diff.aFileContent || '',
    file.diff.bFileContent || '',
    '',
    '',
    { context: DIFF_CONTEXT_LINES }
  )

  if (!patch.hunks.length) return ''

  const lines = [`diff --git a/${oldDisplayPath} b/${newDisplayPath}`]
  if (file.diff.diffType === DiffType.ADD) lines.push('new file mode 100644')
  if (file.diff.diffType === DiffType.DELETE) lines.push('deleted file mode 100644')
  lines.push(`--- ${oldPatchPath}`, `+++ ${newPatchPath}`)

  for (const hunk of patch.hunks) {
    lines.push(
      `@@ -${formatHunkRange(hunk.oldStart, hunk.oldLines)} +${formatHunkRange(
        hunk.newStart,
        hunk.newLines
      )} @@`
    )
    lines.push(...hunk.lines)
  }

  return `${lines.join('\n')}\n`
}

function buildReactDiffFile(file: DiffFileMeta): FileData | null {
  const patch = buildGitPatch(file)
  if (!patch) return null

  const [parsedFile] = parseDiff(patch, { nearbySequences: 'zip' })
  return parsedFile ? { ...parsedFile, type: getReactDiffType(file.diff.diffType) } : null
}

function hasContentChanges(file: DiffFileMeta): boolean {
  if (file.diff.diffType === DiffType.ADD) return Boolean(file.diff.bFileContent)
  if (file.diff.diffType === DiffType.DELETE) return Boolean(file.diff.aFileContent)
  return (file.diff.aFileContent || '') !== (file.diff.bFileContent || '')
}

function getEmptyDiffMessage(file: DiffFileMeta): string {
  if (file.diff.diffType === DiffType.ADD) return 'Empty file added'
  if (file.diff.diffType === DiffType.DELETE) return 'Empty file deleted'
  return 'No line changes in this file'
}

function getChangeAnchorId(change: ChangeData): string {
  return `diff-change-${getChangeKey(change).replace(/[^A-Za-z0-9_-]/g, '-')}`
}

function getChangeLineNumber(change: ChangeData): number {
  if (isInsert(change) || isDelete(change)) return change.lineNumber
  return change.newLineNumber
}

function buildChangeGroups(hunks: HunkData[]): ChangeGroup[] {
  const groups: ChangeGroup[] = []
  let pendingGroup: ChangeGroup | null = null

  const flushGroup = () => {
    if (!pendingGroup) return
    groups.push(pendingGroup)
    pendingGroup = null
  }

  for (const hunk of hunks) {
    for (const change of hunk.changes) {
      if (isInsert(change) || isDelete(change)) {
        const changeKey = getChangeKey(change)
        if (!pendingGroup) {
          pendingGroup = {
            anchorId: getChangeAnchorId(change),
            modifiedLineNumber: getChangeLineNumber(change),
            selectedChangeKeys: [changeKey]
          }
        } else {
          pendingGroup.selectedChangeKeys.push(changeKey)
        }
      } else {
        flushGroup()
      }
    }
    flushGroup()
  }

  return groups
}

function buildHunkTokens(file: DiffFileMeta, hunks: HunkData[]): HunkTokens | null {
  if (file.totalLines > DIFF_HIGHLIGHT_LINE_LIMIT) return null

  const enhancers = [markEdits(hunks, { type: 'line' })]
  const language = getRefractorLanguage(file.path)

  try {
    if (language !== 'plaintext' && refractor.registered(language)) {
      return tokenize(hunks, {
        enhancers,
        highlight: true,
        language,
        oldSource: file.diff.aFileContent || '',
        refractor
      })
    }

    return tokenize(hunks, { enhancers, highlight: false })
  } catch {
    return null
  }
}

function getCollapsedExpansionRanges(previousHunk: HunkData | null, nextHunk: HunkData) {
  const start = previousHunk ? previousHunk.oldStart + previousHunk.oldLines : 1
  const end = nextHunk.oldStart
  const collapsedLines = end - start

  if (collapsedLines <= 0) return []
  if (collapsedLines <= DIFF_EXPAND_FULL_LINE_LIMIT) return [[start, end]]

  if (!previousHunk) {
    return [[Math.max(start, end - DIFF_EXPAND_FULL_LINE_LIMIT), end]]
  }

  const headEnd = Math.min(end, start + DIFF_EXPAND_EDGE_LINE_COUNT)
  const tailStart = Math.max(headEnd, end - DIFF_EXPAND_EDGE_LINE_COUNT)
  return [
    [start, headEnd],
    [tailStart, end]
  ]
}

function DiffCodeViewer({ activeChangeGroup, file, parsedFile }: DiffCodeViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [hunks, setHunks] = useState<HunkData[]>(() => parsedFile.hunks)
  const sourceLines = useMemo(
    () => splitSourceLines(file.diff.aFileContent || file.diff.bFileContent || ''),
    [file.diff.aFileContent, file.diff.bFileContent]
  )
  const tokens = useMemo(() => buildHunkTokens(file, hunks), [file, hunks])

  useEffect(() => {
    setHunks(parsedFile.hunks)
  }, [parsedFile])

  useEffect(() => {
    if (!activeChangeGroup || !containerRef.current) return
    const target = containerRef.current.querySelector(`#${activeChangeGroup.anchorId}`)
    target?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
  }, [activeChangeGroup])

  const expandCollapsedBlock = useCallback(
    (previousHunk: HunkData | null, nextHunk: HunkData) => {
      const ranges = getCollapsedExpansionRanges(previousHunk, nextHunk)
      if (!ranges.length) return

      setHunks(currentHunks =>
        ranges.reduce(
          (expandedHunks, [start, end]) =>
            expandFromRawCode(expandedHunks, sourceLines, start, end),
          currentHunks
        )
      )
    },
    [sourceLines]
  )

  return (
    <div
      ref={containerRef}
      className='landing-diff-code-view'
      data-testid='codex-diff-view'
      role='table'
      aria-label='Code diff'
    >
      <ReactDiffView
        className={cm(
          'landing-react-diff',
          file.diff.diffType === DiffType.ADD && 'landing-react-diff-added-file',
          file.diff.diffType === DiffType.DELETE && 'landing-react-diff-deleted-file'
        )}
        diffType={getReactDiffType(file.diff.diffType)}
        generateAnchorID={getChangeAnchorId}
        hunks={hunks}
        optimizeSelection
        selectedChanges={activeChangeGroup?.selectedChangeKeys ?? []}
        tokens={tokens}
        viewType='unified'
      >
        {renderedHunks =>
          renderedHunks.flatMap((hunk, index) => {
            const previousHunk = index > 0 ? renderedHunks[index - 1] : null
            const collapsedLines = getCollapsedLinesCountBetween(previousHunk, hunk)
            const elements: ReactElement[] = []

            if (collapsedLines > 0) {
              elements.push(
                <Decoration key={`gap-${hunk.content}`}>
                  <button
                    type='button'
                    className='landing-diff-gap-button'
                    onClick={() => expandCollapsedBlock(previousHunk, hunk)}
                  >
                    <ChevronsUpDown className='h-3.5 w-3.5' />
                    {collapsedLines.toLocaleString()} unmodified lines
                  </button>
                </Decoration>
              )
            }

            elements.push(
              <Hunk key={`${hunk.content}-${hunk.oldStart}-${hunk.newStart}`} hunk={hunk} />
            )
            return elements
          })
        }
      </ReactDiffView>
    </div>
  )
}

export function DiffReviewPanel({
  activePath: controlledActivePath,
  initialActivePath,
  diffs,
  className,
  fillHeight = false,
  headerActions,
  hideFileSidebar = false,
  onActivePathChange
}: DiffReviewPanelProps) {
  const [query, setQuery] = useState('')
  const [fileFilter, setFileFilter] = useState<DiffFileFilter>('all')
  const [internalActivePath, setInternalActivePath] = useState(
    () => initialActivePath || (diffs[0] ? getDiffPath(diffs[0]) : '')
  )
  // Lazy useState initialisers run only on mount. If the parent
  // re-renders this panel with a different ``initialActivePath`` —
  // e.g. the user clicks a second file in the surrounding
  // ``FileDropdown`` — the state needs to follow. Without this sync,
  // the panel stays pinned to the first-opened file and every
  // subsequent click in the parent list is silently ignored.
  useEffect(() => {
    if (initialActivePath) {
      setInternalActivePath(initialActivePath)
    }
  }, [initialActivePath])
  const [renderHugeDiff, setRenderHugeDiff] = useState(false)
  const [activeChangeIndex, setActiveChangeIndex] = useState(-1)
  const [isFileSwitcherOpen, setIsFileSwitcherOpen] = useState(false)
  const fileSwitcherRef = useRef<HTMLDivElement | null>(null)
  const activePath = controlledActivePath ?? internalActivePath
  const shouldFillHeight = fillHeight || hideFileSidebar
  const updateActivePath = useCallback(
    (path: string) => {
      setInternalActivePath(path)
      onActivePathChange?.(path)
    },
    [onActivePathChange]
  )

  const fileMetas = useMemo<DiffFileMeta[]>(() => {
    return diffs.map(diff => {
      const path = getDiffPath(diff)
      const { addedLines, removedLines } = getChangedLineStats(diff)
      return {
        diff,
        path,
        fileName: getFileName(path),
        directory: getDirectory(path),
        extension: getExtension(path),
        addedLines,
        removedLines,
        contentChars: (diff.aFileContent || '').length + (diff.bFileContent || '').length,
        totalLines: Math.max(countLines(diff.aFileContent), countLines(diff.bFileContent))
      }
    })
  }, [diffs])

  const fileFilterCounts = useMemo(() => {
    return fileMetas.reduce(
      (acc, file) => {
        acc.all += 1
        if (file.diff.diffType === DiffType.ADD) acc.added += 1
        else if (file.diff.diffType === DiffType.DELETE) acc.deleted += 1
        else acc.modified += 1
        return acc
      },
      { added: 0, all: 0, deleted: 0, modified: 0 }
    )
  }, [fileMetas])

  const reviewStats = useMemo(() => {
    return fileMetas.reduce(
      (acc, file) => {
        acc.addedLines += file.addedLines
        acc.removedLines += file.removedLines
        return acc
      },
      { addedLines: 0, removedLines: 0 }
    )
  }, [fileMetas])

  const fileFilterOptions: Array<{ id: DiffFileFilter; label: string; count: number }> = [
    { id: 'all', label: 'All', count: fileFilterCounts.all },
    { id: 'added', label: 'Added', count: fileFilterCounts.added },
    { id: 'modified', label: 'Modified', count: fileFilterCounts.modified },
    { id: 'deleted', label: 'Deleted', count: fileFilterCounts.deleted }
  ]

  const filteredFiles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    return fileMetas.filter(file => {
      const matchesQuery = !normalizedQuery || file.path.toLowerCase().includes(normalizedQuery)
      const matchesFilter =
        fileFilter === 'all' ||
        (fileFilter === 'added' && file.diff.diffType === DiffType.ADD) ||
        (fileFilter === 'modified' && file.diff.diffType === DiffType.MODIFY) ||
        (fileFilter === 'deleted' && file.diff.diffType === DiffType.DELETE)
      return matchesQuery && matchesFilter
    })
  }, [fileFilter, fileMetas, query])

  const selectedFile = useMemo(() => {
    const fromFiltered = filteredFiles.find(file => file.path === activePath)
    return (
      fromFiltered ??
      filteredFiles[0] ??
      fileMetas.find(file => file.path === activePath) ??
      fileMetas[0]
    )
  }, [activePath, fileMetas, filteredFiles])
  const selectedFilePath = selectedFile?.path ?? ''
  const selectedDiffType = selectedFile?.diff.diffType ?? DiffType.MODIFY
  const selectedOldPath = selectedFile?.diff.aFilePath ?? ''
  const selectedNewPath = selectedFile?.diff.bFilePath ?? ''
  const selectedOldContent = selectedFile?.diff.aFileContent ?? ''
  const selectedNewContent = selectedFile?.diff.bFileContent ?? ''
  const selectedPatch = selectedFile?.diff.patchBA ?? ''

  useEffect(() => {
    if (!selectedFile) return
    if (selectedFile.path !== activePath) {
      updateActivePath(selectedFile.path)
    }
  }, [activePath, selectedFile, updateActivePath])

  useEffect(() => {
    setRenderHugeDiff(false)
    setActiveChangeIndex(-1)
  }, [
    selectedDiffType,
    selectedFilePath,
    selectedNewContent,
    selectedNewPath,
    selectedOldContent,
    selectedOldPath,
    selectedPatch
  ])

  useEffect(() => {
    if (!isFileSwitcherOpen || typeof document === 'undefined') return

    const handlePointerDown = (event: MouseEvent) => {
      if (!fileSwitcherRef.current?.contains(event.target as Node)) {
        setIsFileSwitcherOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFileSwitcherOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isFileSwitcherOpen])

  const selectFile = useCallback(
    (path: string) => {
      updateActivePath(path)
      setIsFileSwitcherOpen(false)
    },
    [updateActivePath]
  )

  const shouldGateHugeDiff = selectedFile
    ? (selectedFile.totalLines > HUGE_DIFF_LINE_LIMIT ||
        selectedFile.contentChars > HUGE_DIFF_CHAR_LIMIT) &&
      !renderHugeDiff
    : false
  const parsedSelectedFile = useMemo(
    () => (selectedFile && !shouldGateHugeDiff ? buildReactDiffFile(selectedFile) : null),
    [selectedFile, shouldGateHugeDiff]
  )
  const changeGroups = useMemo(
    () => (parsedSelectedFile ? buildChangeGroups(parsedSelectedFile.hunks) : []),
    [parsedSelectedFile]
  )

  useEffect(() => {
    setActiveChangeIndex(-1)
  }, [
    changeGroups.length,
    selectedDiffType,
    selectedFilePath,
    selectedNewContent,
    selectedNewPath,
    selectedOldContent,
    selectedOldPath,
    selectedPatch
  ])

  const revealChange = useCallback(
    (direction: 1 | -1) => {
      if (!changeGroups.length) return

      const nextIndex =
        activeChangeIndex < 0
          ? direction > 0
            ? 0
            : changeGroups.length - 1
          : (activeChangeIndex + direction + changeGroups.length) % changeGroups.length
      setActiveChangeIndex(nextIndex)
    },
    [activeChangeIndex, changeGroups]
  )

  if (!selectedFile) {
    return (
      <div className='flex h-full items-center justify-center text-tokens-neutral-text-tertiary'>
        No diff available
      </div>
    )
  }

  const canNavigateChanges = changeGroups.length > 1 && !shouldGateHugeDiff
  const activeChangeGroup = activeChangeIndex >= 0 ? changeGroups[activeChangeIndex] : undefined
  const activeFilePosition = Math.max(
    fileMetas.findIndex(file => file.path === selectedFile.path),
    0
  )
  const filesChangedLabel = `${fileFilterCounts.all.toLocaleString()} ${
    fileFilterCounts.all === 1 ? 'file' : 'files'
  } changed`

  return (
    <div
      className={cm(
        'flex w-full min-w-0 flex-col overflow-hidden rounded-lg border border-[#D8DEE7] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
        shouldFillHeight && 'h-full min-h-0 flex-1',
        className
      )}
    >
      <style dangerouslySetInnerHTML={{ __html: diffReviewPanelCss }} />
      <div
        ref={fileSwitcherRef}
        className='relative z-20 border-b border-[#E6EAF0] bg-white'
        data-testid='diff-review-file-rail'
      >
        <div className='flex min-h-10 flex-nowrap items-center gap-2 px-2.5 py-1.5 max-[760px]:flex-wrap'>
          {!hideFileSidebar && (
            <button
              type='button'
              onClick={() => setIsFileSwitcherOpen(isOpen => !isOpen)}
              aria-expanded={isFileSwitcherOpen}
              aria-label='Open changed files'
              title='Changed files'
              className={cm(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] text-tokens-neutral-icon-hover transition-colors hover:bg-tokens-neutral-fill-fill-6',
                isFileSwitcherOpen && 'bg-tokens-neutral-fill-fill-6'
              )}
            >
              <List className='size-4' />
              <span className='sr-only'>
                {activeFilePosition + 1} of {fileFilterCounts.all.toLocaleString()} files,{' '}
                {reviewStats.addedLines.toLocaleString()} additions
                {reviewStats.removedLines > 0
                  ? `, ${reviewStats.removedLines.toLocaleString()} deletions`
                  : ''}
              </span>
            </button>
          )}

          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1'>
              <Typography
                fontFamily='geistMono'
                fontSize='bodySm'
                fontWeight='semibold'
                className='min-w-0 truncate text-[#24292F]'
              >
                {selectedFile.path}
              </Typography>
              <span className='flex shrink-0 items-center gap-1.5 text-xs'>
                <span className='font-medium text-[#57606A]'>{filesChangedLabel}</span>
                <span className='font-mono font-semibold text-[#1F883D]'>
                  +{reviewStats.addedLines.toLocaleString()}
                </span>
                <span className='font-mono font-semibold text-[#CF222E]'>
                  -{reviewStats.removedLines.toLocaleString()}
                </span>
              </span>
            </div>
          </div>

          <div className='ml-auto flex shrink-0 items-center justify-end gap-1.5'>
            {canNavigateChanges && (
              <div
                className='flex h-8 items-center overflow-hidden rounded-md border border-[#D8DEE7] bg-white shadow-[0_1px_1px_rgba(15,23,42,0.03)]'
                aria-label='Change navigation'
              >
                <button
                  type='button'
                  onClick={() => revealChange(-1)}
                  className='flex size-8 items-center justify-center text-[#6B7280] transition-colors hover:bg-[#F6F8FA] hover:text-[#24292F]'
                  aria-label='Previous change'
                  title='Previous change'
                >
                  <ChevronUp className='h-3.5 w-3.5' />
                </button>
                <div className='h-4 w-px bg-[#D8DEE7]' />
                <button
                  type='button'
                  onClick={() => revealChange(1)}
                  className='flex size-8 items-center justify-center text-[#6B7280] transition-colors hover:bg-[#F6F8FA] hover:text-[#24292F]'
                  aria-label='Next change'
                  title='Next change'
                >
                  <ChevronDown className='h-3.5 w-3.5' />
                </button>
              </div>
            )}
            {headerActions}
          </div>
        </div>

        {!hideFileSidebar && isFileSwitcherOpen && (
          <div className='landing-diff-file-popover absolute left-2.5 top-[calc(100%-3px)] z-30 w-[min(760px,calc(100vw-4rem))] max-w-[calc(100%-1.25rem)] overflow-hidden rounded-lg border border-[#D8DEE7] bg-white shadow-[0_18px_50px_rgba(15,23,42,0.16),0_3px_10px_rgba(15,23,42,0.08)]'>
            <div className='border-b border-[#E6EAF0] bg-[#FBFCFE] p-2.5'>
              <div className='relative'>
                <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#8C959F]' />
                <input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder='Search changed files'
                  className='h-9 w-full rounded-md border border-[#D8DEE7] bg-white pl-8 pr-2 text-xs font-medium text-[#24292F] outline-none transition placeholder:text-[#8C959F] focus:border-[#8C959F] focus:ring-2 focus:ring-[#8C959F]/15'
                />
              </div>

              <div
                className='mt-2 flex flex-wrap items-center gap-1'
                aria-label='Filter changed files'
                data-testid='diff-file-filter'
              >
                {fileFilterOptions.map(option => (
                  <button
                    key={option.id}
                    type='button'
                    onClick={() => setFileFilter(option.id)}
                    disabled={option.count === 0}
                    aria-pressed={fileFilter === option.id}
                    className={cm(
                      'flex h-7 items-center gap-1.5 rounded px-2 text-[11px] font-semibold transition-colors',
                      fileFilter === option.id
                        ? 'bg-[#24292F] text-white shadow-[0_1px_2px_rgba(15,23,42,0.12)]'
                        : 'text-[#6B7280] hover:bg-[#F1F3F6] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent'
                    )}
                  >
                    <span>{option.label}</span>
                    <span
                      className={cm(
                        'font-mono text-[10px] font-semibold',
                        fileFilter === option.id ? 'text-white/75' : 'text-[#8C959F]'
                      )}
                    >
                      {option.count.toLocaleString()}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div
              className='overflow-y-auto overflow-x-hidden overscroll-contain p-1.5 [scrollbar-gutter:stable]'
              data-testid='diff-review-file-list'
              style={{ maxHeight: 'min(440px, calc(100vh - 220px))' }}
            >
              {filteredFiles.length === 0 ? (
                <div className='px-3 py-6 text-center text-xs text-tokens-neutral-text-tertiary'>
                  No matching files
                </div>
              ) : (
                <div className='space-y-1'>
                  {filteredFiles.map(file => {
                    const config = getDiffTypeConfig(file.diff.diffType)
                    const FileIcon = config.icon
                    const isActive = file.path === selectedFile.path
                    return (
                      <button
                        key={`${file.path}-${file.diff.diffType}`}
                        type='button'
                        onClick={() => selectFile(file.path)}
                        className={cm(
                          'group flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors',
                          isActive
                            ? 'border-[#8C959F] bg-[#F6F8FA] shadow-[inset_3px_0_0_#24292F]'
                            : 'border-transparent hover:border-[#D8DEE7] hover:bg-[#F8FAFC]'
                        )}
                      >
                        <div
                          className={cm(
                            'flex size-6 shrink-0 items-center justify-center rounded-md border',
                            config.pillClass
                          )}
                        >
                          <FileIcon className='h-3.5 w-3.5' />
                        </div>
                        <div className='min-w-0 flex-1'>
                          <div className='truncate text-xs font-semibold text-[#24292F]'>
                            {file.fileName}
                          </div>
                          {file.directory && (
                            <div className='truncate font-mono text-[10px] text-[#6B7280]'>
                              {file.directory}
                            </div>
                          )}
                        </div>
                        <div className='flex shrink-0 items-center gap-1 font-mono text-[11px] font-semibold'>
                          {file.addedLines > 0 && (
                            <span className='text-[#1F883D]'>+{file.addedLines}</span>
                          )}
                          {file.removedLines > 0 && (
                            <span className='text-[#CF222E]'>-{file.removedLines}</span>
                          )}
                          {file.addedLines === 0 && file.removedLines === 0 && (
                            <span className='font-sans text-[10px] font-medium text-[#8C959F]'>
                              0
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <section
        className={cm('flex min-h-0 min-w-0 flex-col bg-white', shouldFillHeight && 'flex-1')}
        data-testid='diff-review-detail-pane'
      >
        <div
          className={cm(
            'landing-diff-scroll-container min-h-0 overflow-auto overscroll-x-contain overscroll-y-auto bg-white [scrollbar-gutter:stable]',
            shouldFillHeight ? 'h-full max-h-none flex-1' : 'max-h-[min(72vh,820px)]'
          )}
          data-testid='diff-review-scroll-container'
        >
          {shouldGateHugeDiff ? (
            <div className='flex min-h-[360px] items-center justify-center p-6 text-center'>
              <div className='max-w-sm rounded-lg border border-[#DFE3EA] bg-[#F8FAFC] p-4'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMdMedium'
                  fontWeight='semibold'
                  className='text-tokens-neutral-text-primary'
                >
                  Very large diff
                </Typography>
                <div className='mt-2 text-sm text-tokens-neutral-text-secondary'>
                  {selectedFile.totalLines.toLocaleString()} lines in this file.
                </div>
                <div className='mt-1 text-xs text-tokens-neutral-text-tertiary'>
                  Unchanged regions will be collapsed, but this file is large enough that diff
                  computation may still take time.
                </div>
                <button
                  type='button'
                  onClick={() => setRenderHugeDiff(true)}
                  className='mt-4 h-9 rounded-md bg-[#262626] px-3 text-sm font-semibold text-white hover:bg-[#3A3A3A]'
                >
                  Open collapsed diff
                </button>
              </div>
            </div>
          ) : (
            <div className='min-w-[760px]' data-testid='diff-review-scroll-content'>
              {!hasContentChanges(selectedFile) ? (
                <div
                  className='flex min-h-[360px] items-center justify-center text-sm text-tokens-neutral-text-secondary'
                  data-testid='codex-diff-view-empty'
                >
                  {getEmptyDiffMessage(selectedFile)}
                </div>
              ) : parsedSelectedFile ? (
                <DiffCodeViewer
                  activeChangeGroup={activeChangeGroup}
                  file={selectedFile}
                  parsedFile={parsedSelectedFile}
                />
              ) : (
                <div
                  className='flex min-h-[360px] items-center justify-center text-sm text-tokens-neutral-text-secondary'
                  data-testid='codex-diff-view-empty'
                >
                  No renderable line changes in this file
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default DiffReviewPanel
